/**
 * selectSegmentById — mark exactly one Segment as the open one.
 *
 * This is the builder's canonical "switch the canvas to Segment X" transform,
 * extracted so it can be asserted on its own. The idiom it encodes is the one
 * the app already uses when a Segment is deleted or duplicated
 * (`components/SegmentBuilder/Main/SegmentSetting/index.jsx`): rebuild the
 * `wheel_segments` array with `selected` true on the target and false on every
 * other row.
 *
 * WHY `selected` IS THE SWITCH, and not something else. The craft `<Frame>` that
 * holds the canvas is KEYED by the selected Segment's id
 * (`components/SegmentView/index.jsx`, the `BUILDER_EMPTY_SEGMENT_FIX` key), and
 * the builder writes craft's `editorState` back into the `wheelData` row whose
 * `id === selectedArc.id` (`pages/SegmentBuilder/index.jsx`, both the live sync
 * and the save payload). So the row this function marks is, by construction, the
 * Segment whose content the canvas edits and persists.
 *
 * Getting this wrong is not a cosmetic bug: an insert performed against the
 * wrong active Segment silently writes a user's capture into a Segment they were
 * not looking at, and they would only discover it later. Hence one small pure
 * function with its own tests, rather than an inline `.map` at the call site.
 */

/**
 * @param {Array<object>} wheelSegments the builder's `wheel_segments` array
 * @param {*} segmentId                 id of the Segment to open
 * @returns {Array<object>} a NEW array; the input is never mutated. Returns the
 *   input unchanged (same reference) when it is not an array, and returns a
 *   copy with every row deselected when `segmentId` matches nothing — never a
 *   silently half-applied state.
 */
export const selectSegmentById = (wheelSegments, segmentId) => {
  if (!Array.isArray(wheelSegments)) return wheelSegments;
  return wheelSegments.map((segment) => ({
    ...segment,
    selected: !!segment && segment.id === segmentId,
  }));
};

/** Is `segmentId` actually a row in this list? Guards a stale picker choice. */
export const containsSegmentId = (wheelSegments, segmentId) =>
  Array.isArray(wheelSegments) &&
  wheelSegments.some((segment) => segment && segment.id === segmentId);

export default selectSegmentById;
